import { useLocale, useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import {
  ENERGY_LABEL_CLASSES,
  formatEnergyLabelBound,
} from "./energy-label-scale";

type Props = {
  /** Class to mark as "this home"; -1 shows the plain scale. */
  activeIndex?: number;
  /** The home's primary fossil energy use, shown in the marker when known. */
  activeValue?: number | null;
  className?: string;
};

/**
 * The official class ladder as arrow bars, the way the paper label prints it,
 * with each class's upper bound and the searched home marked. Works in server
 * and client trees.
 */
export default function EnergyLabelLadder({
  activeIndex = -1,
  activeValue = null,
  className,
}: Props) {
  const t = useTranslations("energy-label");
  const locale = useLocale();
  const last = ENERGY_LABEL_CLASSES.length - 1;

  return (
    <figure className={cn("flex flex-col gap-3", className)}>
      <div className="flex items-baseline justify-between gap-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        <span>{t("scale-title")}</span>
        <span className="normal-case tracking-normal">{t("scale-unit")}</span>
      </div>
      <ol className="flex flex-col gap-1" aria-label={t("scale")}>
        {ENERGY_LABEL_CLASSES.map((item, index) => {
          const active = index === activeIndex;
          const dimmed = activeIndex >= 0 && !active;
          return (
            <li
              key={item.label}
              aria-current={active ? "true" : undefined}
              className={cn(
                "flex items-center gap-3 transition-opacity",
                dimmed && "opacity-40",
              )}
            >
              <span className="flex min-w-0 flex-1 items-center gap-2">
                <span
                  className="flex h-6 flex-none items-center pl-2.5 text-xs font-bold [clip-path:polygon(0_0,calc(100%-10px)_0,100%_50%,calc(100%-10px)_100%,0_100%)]"
                  style={{
                    width: `${28 + (index / last) * 40}%`,
                    background: item.color,
                    color: item.ink,
                  }}
                >
                  {item.label}
                </span>
                {active && (
                  <span className="inline-flex min-w-0 items-center truncate rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground">
                    {activeValue !== null
                      ? t("this-home-value", {
                          value: new Intl.NumberFormat(locale, {
                            maximumFractionDigits: 0,
                          }).format(activeValue),
                        })
                      : t("this-home")}
                  </span>
                )}
              </span>
              <span
                className={cn(
                  "w-12 flex-none text-right text-[11px] tabular-nums text-muted-foreground",
                  active && "font-semibold text-foreground",
                )}
              >
                {formatEnergyLabelBound(index, locale)}
              </span>
            </li>
          );
        })}
      </ol>
      <figcaption className="text-xs leading-5 text-muted-foreground">
        {t("scale-note")}
      </figcaption>
    </figure>
  );
}
